import { useState, useEffect } from 'react';
import { Search, MapPin, Briefcase, Filter, ChevronRight, Building2, Clock, Euro } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RechercheEmploiPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: métier, 2: lieu, 3: résultats

  const metiers = [
    'Électricien', 'Plombier', 'Maçon', 'Carreleur', 'Peintre',
    'Menuisier', 'Couvreur', 'Chauffagiste', 'Charpentier', 'Serrurier'
  ];

  const villes = [
    'Paris', 'Lyon', 'Marseille', 'Toulouse', 'Bordeaux',
    'Nantes', 'Strasbourg', 'Lille', 'Nice', 'Montpellier'
  ];

  const handleSearch = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/jobs/search?q=${searchQuery}&location=${location}`);
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs || []);
      }
    } catch (e) {
      console.error('Search error:', e);
    } finally {
      setLoading(false);
      setStep(3);
    }
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-orange-50 to-white p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
            <Search className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Trouve ton emploi en 3 clics
          </h1>
          <p className="text-gray-600">
            {step === 1 && 'Étape 1/3 : Quel métier recherches-tu ?'}
            {step === 2 && 'Étape 2/3 : Où cherches-tu ?'}
            {step === 3 && 'Étape 3/3 : Résultats'}
          </p>
        </div>

        {/* Progress bar */}
        <div className="flex gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div
              key={s}
              className={`h-2 flex-1 rounded-full transition-colors ${
                s <= step ? 'bg-orange-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Métier */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher un métier..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {metiers.map(m => (
                <button
                  key={m}
                  onClick={() => { setSearchQuery(m); setStep(2); }}
                  className={`p-4 rounded-xl border text-left transition-all hover:border-orange-500 hover:bg-orange-50 ${
                    searchQuery === m ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                  }`}
                >
                  <Briefcase size={20} className="text-orange-500 mb-2" />
                  <span className="font-medium text-gray-900">{m}</span>
                </button>
              ))}
            </div>
            {searchQuery && (
              <Button onClick={() => setStep(2)} className="w-full bg-orange-500 hover:bg-orange-600">
                Continuer <ChevronRight size={18} />
              </Button>
            )}
          </div>
        )}

        {/* Step 2: Lieu */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Ville, département ou région..."
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {villes.map(v => (
                <button
                  key={v}
                  onClick={() => { setLocation(v); }}
                  className={`p-4 rounded-xl border text-left transition-all hover:border-orange-500 hover:bg-orange-50 ${
                    location === v ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                  }`}
                >
                  <MapPin size={20} className="text-orange-500 mb-2" />
                  <span className="font-medium text-gray-900">{v}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Retour
              </Button>
              <Button onClick={handleSearch} className="flex-1 bg-orange-500 hover:bg-orange-600">
                Rechercher <Search size={18} />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Résultats */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-600">
                {jobs.length} offre{jobs.length > 1 ? 's' : ''} trouvée{jobs.length > 1 ? 's' : ''}
              </p>
              <Button variant="outline" size="sm" onClick={() => setStep(1)}>
                Nouvelle recherche
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto" />
                <p className="mt-4 text-gray-500">Recherche en cours...</p>
              </div>
            ) : jobs.length > 0 ? (
              <div className="space-y-3">
                {jobs.map((job, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <h3 className="font-bold text-gray-900 mb-1">{job.title}</h3>
                    <p className="text-orange-600 font-medium mb-2">{job.company}</p>
                    <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin size={14} /> {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} /> {job.type}
                      </span>
                      {job.salary && (
                        <span className="flex items-center gap-1">
                          <Euro size={14} /> {job.salary}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl">
                <Briefcase size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Aucune offre trouvée pour cette recherche</p>
                <p className="text-sm text-gray-400 mt-1">Essayez avec d'autres critères</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
